import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { 
  Home, 
  FileText, 
  User, 
  Map, 
  Folder, 
  Tag, 
  Calendar,
  ArrowRight,
  ArrowLeft,
  Clock,
  ChevronRight
} from 'lucide-react';

interface Post {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categoryName?: string;
  tags: string[];
  date: string;
  readingTime: number;
  views?: number;
}

const Sitemap: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch('/api/blog-admin/posts');
        const data = await res.json();
        if (data.success) {
          setPosts(data.data || []);
        }
      } catch (error) {
        console.error('获取文章列表失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const categories = [...new Set(posts.map(post => post.category || '未分类'))];
  const allTags = [...new Set(posts.flatMap(post => post.tags || []))];

  const recentPosts = [...posts]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 10);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 dark:from-[#0b1220] dark:via-slate-900 dark:to-slate-800 py-8 sm:py-12 px-3 sm:px-4">
      <SEO 
        title="网站地图"
        description="浏览 lingdux.space 的完整网站结构，快速找到您需要的内容。"
      />
      <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
        
        {/* Header */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10"
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            返回首页
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30">
              <Map className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">网站地图</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">快速导航 · 完整站点结构</p>
            </div>
          </div>
          
          <p className="text-slate-600 dark:text-slate-300 leading-7">
            本页面展示网站的完整结构，帮助您快速找到所需内容。
          </p>
        </motion.section>

        {/* Main Navigation */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-5"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Home className="w-5 h-5 text-blue-500" />
            主要页面
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/"
              className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 dark:from-blue-500/20 dark:to-cyan-500/20 border border-blue-200/50 dark:border-blue-800/50 hover:-translate-y-0.5 hover:shadow-md transition-all"
            >
              <Home className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 dark:text-white">首页</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">网站主页面</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link
              to="/blog"
              className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 dark:from-emerald-500/20 dark:to-teal-500/20 border border-emerald-200/50 dark:border-emerald-800/50 hover:-translate-y-0.5 hover:shadow-md transition-all"
            >
              <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 dark:text-white">博客</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">文章列表</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link
              to="/about"
              className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-violet-500/10 to-purple-500/10 dark:from-violet-500/20 dark:to-purple-500/20 border border-violet-200/50 dark:border-violet-800/50 hover:-translate-y-0.5 hover:shadow-md transition-all"
            >
              <User className="w-5 h-5 text-violet-600 dark:text-violet-400" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 dark:text-white">关于</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">关于我和联系</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link
              to="/privacy"
              className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 dark:from-sky-500/20 dark:to-blue-500/20 border border-sky-200/50 dark:border-sky-800/50 hover:-translate-y-0.5 hover:shadow-md transition-all"
            >
              <FileText className="w-5 h-5 text-sky-600 dark:text-sky-400" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 dark:text-white">隐私政策</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">隐私保护说明</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link
              to="/terms"
              className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 dark:from-amber-500/20 dark:to-orange-500/20 border border-amber-200/50 dark:border-amber-800/50 hover:-translate-y-0.5 hover:shadow-md transition-all"
            >
              <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 dark:text-white">使用条款</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">网站使用条件</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
            <Link
              to="/sitemap"
              className="flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-rose-500/10 to-pink-500/10 dark:from-rose-500/20 dark:to-pink-500/20 border border-rose-200/50 dark:border-rose-800/50 hover:-translate-y-0.5 hover:shadow-md transition-all"
            >
              <Map className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              <div className="flex-1">
                <div className="font-semibold text-slate-800 dark:text-white">网站地图</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">当前页面</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
          </div>
        </motion.section>

        {/* Recent Posts */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-5"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-500" />
            最新文章
          </h2>
          
          {loading ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">加载中...</div>
          ) : recentPosts.length > 0 ? (
            <div className="space-y-3">
              {recentPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="flex items-center gap-4 p-4 rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-slate-50/50 dark:bg-slate-800/30 hover:bg-white dark:hover:bg-slate-800/60 hover:-translate-y-0.5 hover:shadow-sm transition-all"
                >
                  <div className="p-2 rounded-lg bg-emerald-100/80 dark:bg-emerald-900/30">
                    <FileText className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 dark:text-white truncate">
                      {post.title}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.date)}
                      </span>
                      <span>{post.categoryName || post.category}</span>
                      <span>{post.readingTime} 分钟阅读</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              暂无文章
            </div>
          )}
          
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mt-2"
          >
            查看全部文章
            <ChevronRight className="w-4 h-4" />
          </Link>
        </motion.section>

        {/* Categories & Tags */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-5"
          >
            <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Folder className="w-5 h-5 text-violet-500" />
              分类
            </h2>
            {categories.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat}
                    to="/blog"
                    className="inline-flex items-center gap-1.5 rounded-full border border-violet-200/60 dark:border-violet-800/60 bg-violet-50/50 dark:bg-violet-900/20 px-4 py-2 text-sm font-medium text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors"
                  >
                    {cat}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-slate-500 dark:text-slate-400">暂无分类</div>
            )}
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-5"
          >
            <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Tag className="w-5 h-5 text-amber-500" />
              标签
            </h2>
            {allTags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {allTags.map((tag) => (
                  <Link
                    key={tag}
                    to="/blog"
                    className="inline-flex items-center gap-1.5 rounded-full border border-amber-200/60 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-900/20 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-slate-500 dark:text-slate-400">暂无标签</div>
            )}
          </motion.section>
        </div>

      </div>
    </div>
  );
};

export default Sitemap;
